import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9qqq4me3oq4jqumtp50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (
        response.ok &&
        parsed &&
        parsed.code === 'OK'
      ) {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-6">关于我们</h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.intro}
            </p>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 text-center">我们的使命</h2>
            <p className="text-sm md:text-base text-foreground-600 text-center max-w-2xl mx-auto leading-relaxed mb-14">
              {aboutInfo.mission}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {aboutInfo.values.map((val, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-300 transition-colors"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-500 mb-4">
                    <i className={`${val.icon} text-lg`}></i>
                  </span>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 text-center">创作者口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-12">听听创作者们怎么说</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {aboutInfo.testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 text-center">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              有任何问题或建议？欢迎随时联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 mx-auto mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">邮箱</div>
                <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</div>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 mx-auto mb-3">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">微信</div>
                <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.wechat}</div>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 mx-auto mb-3">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">地址</div>
                <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</div>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 border border-background-200 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 border border-background-200 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 border border-background-200 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-background-200 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（不超过500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <div className="honeypot-wrapper">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                发送消息
              </button>

              {formStatus === 'success' && (
                <p className="mt-4 text-sm text-green-600 text-center">消息已成功发送，我们会尽快回复您！</p>
              )}
              {formStatus === 'error' && (
                <p className="mt-4 text-sm text-accent-600 text-center">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}